import { useMemo, useState } from 'react';
import { Badge, Tooltip } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { MessageSquare01 } from '@insight-health-ai/roseate/icons';
import { Button, SkeletonLoader } from '@insight-health-ai/roseate/components';
import { CommentEntityType, Practitioner } from 'src/models';
import { useGetCommentList } from 'src/hooks/comment';
import { useGetPractitionerList } from 'src/hooks/practitioner';
import { CommentItem } from './commentItem';
// import { CommentsDrawer } from './CommentsDrawer';

dayjs.extend(relativeTime);

type CommentsIndicatorProps = {
  entityType: CommentEntityType;
  entityId: string;
  count?: number;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
};

export const CommentsIndicator = ({
  entityType,
  entityId,
  count,
  onClick,
}: CommentsIndicatorProps) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [hasHovered, setHasHovered] = useState(false);

  const { data, isLoading } = useGetCommentList(
    { entityType, entityId, perPage: 50 },
    { enabled: hasHovered }
  );
  const { data: practitionerListData } = useGetPractitionerList(
    { is_shown: true },
    { enabled: hasHovered }
  );

  const practitionerByIdMap = useMemo(() => {
    const map = new Map<number, Practitioner>();
    practitionerListData?.practitioners?.forEach((p) =>
      map.set(p.practitioner_id, p)
    );
    return map;
  }, [practitionerListData?.practitioners]);

  if (!count || count <= 0) return null;

  const comments = data?.comments ?? [];
  const latest = comments[comments.length - 1];

  const renderTooltipContent = () => {
    if (isLoading || !data) {
      return (
        <div className="w-48">
          <SkeletonLoader active paragraph={{ rows: 2 }} title={false} />
        </div>
      );
    }
    if (!latest) {
      return <div className="text-xs">No notes yet.</div>;
    }

    return (
      <div className="flex flex-col gap-1 max-w-xs">
        <CommentItem
          comment={latest}
          practitionerByIdMap={practitionerByIdMap}
        />
        {count > 1 && (
          <div className="text-xs opacity-70 mt-1">
            {count} comments - click to see all
          </div>
        )}
      </div>
    );
  };

  return (
    <Tooltip
      title={renderTooltipContent()}
      open={tooltipOpen}
      onOpenChange={(open) => {
        setTooltipOpen(open);
        if (open) setHasHovered(true);
      }}
      placement="topRight"
    >
      <Badge
        count={
          <div className="w-4 h-4 flex items-center justify-center text-xs bg-danger-surface-hover rounded-full">
            {count}
          </div>
        }
        size="small"
        offset={[-5, 3]}
      >
        <Button
          variant="text"
          icon={<MessageSquare01 className="text-lg" />}
          onClick={(e) => {
            setTooltipOpen(false);
            onClick?.(e);
          }}
        />
      </Badge>
    </Tooltip>
  );
};
